import React, { useRef, useState } from 'react';
import { Loader2, Palette, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { oapi } from '@/lib/officialsApi';
import { DEFAULT_SHARE_THEME, HEX_RE, SHARE_THEME_PRESETS, shareHero } from '@/lib/shareTheme';

const ColorField = ({ label, value, onChange, testId }) => {
  const valid = HEX_RE.test(value);
  return (
    <label className="flex-1 min-w-0">
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</span>
      <span className={`flex items-center gap-2 rounded-md border px-2 h-10 focus-within:ring-2 focus-within:ring-[#00247D]/30 ${valid ? 'border-slate-200' : 'border-red-400'}`}>
        <input type="color" value={valid ? value : '#000000'} onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent p-0" data-testid={`${testId}-picker`} aria-label={`${label} color`} />
        <input value={value} onChange={(e) => onChange(e.target.value.trim().toUpperCase())} maxLength={7}
          className="w-full min-w-0 font-mono text-sm uppercase outline-none" data-testid={`${testId}-hex`} aria-label={`${label} hex code`}
          aria-invalid={!valid} />
      </span>
      {!valid && <span className="block text-[11px] text-red-600 mt-1" data-testid={`${testId}-error`}>Use a code like #1E3A8A</span>}
    </label>
  );
};

const Preview = ({ theme, eventName }) => {
  const h = shareHero(theme);
  return (
    <div className="rounded-lg overflow-hidden border border-slate-200" data-testid="share-colors-preview">
      <div className="border-b-4 px-4 py-3 transition-[background] duration-300" style={h.header}>
        <div className="text-xl font-black leading-none truncate" style={{ fontFamily: 'Barlow Condensed', color: h.accent }}>
          {(eventName || 'Your League').toUpperCase()}
        </div>
        <div className={`text-[11px] mt-1.5 ${h.text}`}>Public schedule header preview</div>
      </div>
      <div className="bg-slate-50 px-2 pt-2">
        <div className="flex justify-between rounded-t-md px-3 py-1.5 text-[11px] font-semibold" style={h.tableHead} data-testid="share-colors-preview-table">
          <span>Date</span><span>Time</span><span>Home</span><span>Score</span><span>Away</span>
        </div>
      </div>
    </div>
  );
};

export function ShareHeaderColors({ eventId, eventName, initialTheme, onSaved }) {
  const [theme, setTheme] = useState(initialTheme || DEFAULT_SHARE_THEME);
  const [saving, setSaving] = useState(false);
  const set = (key) => (v) => setTheme((t) => ({ ...t, [key]: v }));
  const valid = HEX_RE.test(theme.primary) && HEX_RE.test(theme.accent);
  const lastValid = useRef(theme);
  if (valid) lastValid.current = theme;

  const save = async (body, msg) => {
    setSaving(true);
    try {
      const res = await oapi(`/events/${eventId}/share-theme`, { method: 'PUT', body });
      setTheme(res.share_theme || DEFAULT_SHARE_THEME);
      onSaved?.(res.share_theme);
      toast.success(msg);
    } catch (e) {
      toast.error(e.message || 'Could not save the colors');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="share-header-colors">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Palette className="w-4 h-4 text-[#00247D]" /> Header colors
      </div>
      <Preview theme={lastValid.current} eventName={eventName} />
      <div className="flex flex-wrap gap-1.5" data-testid="share-colors-presets">
        {SHARE_THEME_PRESETS.map((p) => {
          const active = p.primary === theme.primary && p.accent === theme.accent;
          return (
            <button key={p.name} type="button" title={p.name} onClick={() => setTheme({ primary: p.primary, accent: p.accent })}
              className={`w-8 h-8 rounded-full border-2 overflow-hidden transition-transform hover:scale-110 ${active ? 'border-[#00247D] scale-110' : 'border-slate-200'}`}
              style={{ background: `linear-gradient(135deg, ${p.primary} 55%, ${p.accent} 55%)` }}
              data-testid={`share-colors-preset-${p.name.toLowerCase()}`} aria-label={`${p.name} colors`} />
          );
        })}
      </div>
      <div className="flex gap-2">
        <ColorField label="Header" value={theme.primary} onChange={set('primary')} testId="share-color-primary" />
        <ColorField label="Accent" value={theme.accent} onChange={set('accent')} testId="share-color-accent" />
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" className="flex-1" disabled={saving}
          onClick={() => save({ primary: null, accent: null }, 'Header colors reset')} data-testid="share-colors-reset">
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
        </Button>
        <Button type="button" size="sm" className="flex-1 bg-[#00247D] hover:bg-[#001A5C]" disabled={saving || !valid}
          onClick={() => save(theme, 'Header colors saved')} data-testid="share-colors-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null} Save colors
        </Button>
      </div>
    </div>
  );
}
